/**
 * WineCard.js — Goosecross wine listing card.
 *
 * OVERRIDES: src/components/base/WineCard.js
 *
 * MIGRATION PARITY (old createWineListing, script.js 1871-1923):
 *   - Image at top, clickable → detail view (in-stock only).
 *   - Pill-shaped "Tasting notes" button (calendar icon + outlined border)
 *     above the title (in-stock only).
 *   - Title (bold, single dark color).
 *   - Bottom row: price + size stacked on the LEFT, "BUY NOW" button on the
 *     RIGHT. The button sizes to its content — NOT full-width.
 *   - No Appellation / Varietal / Alcohol meta lines (the old card showed
 *     only the essentials).
 *   - SOLD OUT: bottom-left burgundy overlay on the image, no tasting-notes
 *     button, no CTA. Whole card is clickable → detail view.
 *
 * INTERFACE: render(wine) → HTMLElement, same signature as base.
 *
 * Reference: goosecross/script.js Lines 1871-1923 (createWineListing).
 */

import { emit ,isChatDisabled} from "../../../core/state.js";
import { bindWineDrawerOpen } from "../../../utils/openWineDrawer.js";

// Original Goosecross tasting-notes notebook icon.
const NOTES_ICON = `<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="15" viewBox="0 0 12 15" fill="none">
                      <path d="M3.16667 0.5V3.16667M5.83333 0.5V3.16667M8.5 0.5V3.16667M3.16667 5.83333H7.16667M3.16667 8.5H8.5M3.16667 11.1667H6.5M1.83333 1.83333H9.83333C10.5697 1.83333 11.1667 2.43029 11.1667 3.16667V12.5C11.1667 13.2364 10.5697 13.8333 9.83333 13.8333H1.83333C1.09695 13.8333 0.5 13.2364 0.5 12.5V3.16667C0.5 2.43029 1.09695 1.83333 1.83333 1.83333Z" stroke="#6E1811" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>`;

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}


function metaLine(wine) {
  const notes = wine.winemaker_notes || {};
  return [notes.Vintage, notes.Alcohol || notes.alcohol || "", wine.wine_type || ""]
    .filter(Boolean)
    .join(" | ");
}
export default class WineCard {
  render(wine) {
    const card = document.createElement("div");
    card.className = "ws-card ws-wine-card ws-goose-winecard";
    card.setAttribute("data-variant-id", wine.variant_id ?? wine["Variant SKU"] ?? "");
    card.setAttribute("data-cta-link", wine.cta_link);
    if (wine.unique_id != null) card.dataset.uniqueId = String(wine.unique_id);
    if (wine.wine_key) card.dataset.wineKey = String(wine.wine_key);

    const soldOut = isTrue(wine.sold_out);
    const unavailable = wine.available !== undefined && !isTrue(wine.available);
    const detailLabel = wine.message || `Tell me more about ${wine.title || "this wine"}`;
     const openDetail = () => {

    if (isChatDisabled()) return;

    emit("ws-prompt-select", {
      label: detailLabel,
      value: detailLabel,
      uniqueId: wine.unique_id,
    });
  };


    // ── head: image + sold-out overlay ──
    const head = document.createElement("div");
    head.className = "ws-card-head ws-goose-head";

    const imageArea = document.createElement("div");
    imageArea.className = "ws-goose-image-area";
    head.appendChild(imageArea);

     // Tasting-notes pill (in-stock only).
      const tasting = document.createElement("button");
      tasting.type = "button";
      tasting.className = "ws-goose-tasting";
      tasting.innerHTML = `${NOTES_ICON}<span>Tasting notes</span>`;
      bindWineDrawerOpen(tasting, wine, card);
      // body.appendChild(tasting);

    if (wine.cover_image_link) {
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = wine.cover_image_link;
      img.alt = wine.title || "Wine";
      img.loading = "lazy";
      if (!soldOut) {
        img.style.cursor = "pointer";
        img.addEventListener("click", openDetail);
      }
      imageArea.appendChild(img);
      head.appendChild(tasting);
    }
    if (soldOut) {
      const overlay = document.createElement("span");
      overlay.className = "ws-goose-soldout";
      overlay.textContent = "SOLD OUT";
      imageArea.appendChild(overlay);
    }
    card.appendChild(head);

    // ── body ──
    const body = document.createElement("div");
    body.className = "ws-card-body ws-goose-body";



    if (wine.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title ws-goose-winetitle";
      title.title = wine.title;
      title.textContent = wine.title;
      body.appendChild(title);
    }

    const meta = metaLine(wine);
    if (meta) {
      const desc = document.createElement("p");
      desc.className = "ws-goose-winecard-meta";
      desc.textContent = meta;
      body.appendChild(desc);
    }

    // Bottom row: [price + size on the left]  [BUY NOW on the right]
    const bottom = document.createElement("div");
    bottom.className = "ws-goose-bottom";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-goose-priceblock";
    if (wine.regular_price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = wine.regular_price;
      priceBlock.appendChild(price);
    }
    // Old createWineListing product-footer: price + bottle_quantity stacked.
    if (wine.bottle_quantity) {
      const qty = document.createElement("p");
      qty.className = "ws-card-unit ws-goose-bottle-qty";
      qty.textContent = wine.bottle_quantity;
      priceBlock.appendChild(qty);
    } else if (wine.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = wine.price_unit;
      priceBlock.appendChild(unit);
    }
    if (priceBlock.children.length) bottom.appendChild(priceBlock);

    // External "Add to cart" / "BUY NOW" link (goose has no in-widget cart).
    if (wine.cta_link && !soldOut && !unavailable) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta ws-goose-cta";
      cta.href = wine.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = wine.cta_text || "BUY NOW";
      bottom.appendChild(cta);
    }

    if (bottom.children.length) body.appendChild(bottom);

    card.appendChild(body);

    // Sold-out: whole card → detail view (old createWineListing wrapped the
    // .card in an onclick on the sold-out branch).
    if (soldOut) {
      card.style.cursor = "pointer";
      card.addEventListener("click", (e) => {
        if (e.target.closest("a, button")) return;
        openDetail();
      });
    }

    return card;
  }
}
